import { useState, type FormEvent } from 'react'
import { Link } from 'react-router'

import { AuthChrome, PasswordField } from '../../components/AuthChrome'
import { Button, Field, Notice } from '../../components/ui'
import { useAuth } from '../../contexts/AuthContext'
import { ForcedReset } from './ForcedReset'

export function SignIn() {
  const { signIn, signOut, authError } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [needsNewPassword, setNeedsNewPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)

  // The forced-reset challenge renders in place rather than at its own route.
  // AIVY's reason, kept: the challenge lives in Amplify's in-memory sign-in
  // state, so a navigation that remounts the tree loses it and the user is
  // returned to a sign-in form that will not accept their temporary password.
  if (needsNewPassword) return <ForcedReset onDone={() => setNeedsNewPassword(false)} />

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!email.trim() || !password) {
      setLocalError('Enter your email and password.')
      return
    }
    setLocalError(null)
    setIsSubmitting(true)
    try {
      const result = await signIn(email, password)
      if (result === 'new_password_required') setNeedsNewPassword(true)
      // On 'signed_in' the account is already loaded; the route guard moves the
      // user on from resolved state rather than from a navigate() here, which
      // is what stops a freshly-authenticated user bouncing back to sign-in.
    } catch {
      // authError carries it — surfaced below.
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <AuthChrome
      eyebrow="Welcome back"
      title="Sign in to your baseline"
      lead="Pick up where your brain left off."
      footer={
        <>
          <span>
            New to BrainSpot? <Link className="auth__link" to="/sign-up">Create your baseline</Link>
          </span>
          {/* Orphaned-session recovery. Without it a user whose tokens resolve
              but whose account does not can be permanently wedged here. */}
          <button type="button" className="auth__link auth__link--block" onClick={() => void signOut()}>
            Having trouble? Clear this session and start again
          </button>
        </>
      }
    >
      <form className="auth__form" onSubmit={handleSubmit} noValidate>
        {(localError || authError) && <Notice tone="error">{localError ?? authError}</Notice>}

        <Field
          label="Email"
          type="email"
          value={email}
          autoComplete="username"
          autoFocus
          onChange={(e) => setEmail(e.target.value)}
        />
        <PasswordField label="Password" value={password} onChange={setPassword} />

        <div className="auth__row">
          <Link className="auth__link" to="/forgot-password">Forgot password?</Link>
        </div>

        <Button type="submit" full busy={isSubmitting}>
          {isSubmitting ? 'Signing in' : 'Sign in'}
        </Button>
      </form>
    </AuthChrome>
  )
}
